import styles from './segmented-control.scss?inline';
import slotStyles from './segmented-control.slot.scss?inline';
import { LitElement, unsafeCSS, CSSResult } from 'lit';
import { literal, html } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import { appendStylesheet, c11nElement } from '../core';
import { property, state } from 'lit/decorators.js';
import { SegmentComponent, C11nSegment } from './segment';

export type SegmentSize = 'small' | 'medium' | 'large';
export type SegmentColor = 'neutral' | 'yellow' | 'turquoise';

@c11nElement()
export class SegmentedControlComponent extends LitElement {

  static selector = `${C11nPrefix}-segmented-control`;
  static literal = literal`${C11nPrefixLiteral}-segmented-control`;
  static override styles: CSSResult = unsafeCSS(styles);
  
  @property({ attribute: 'c11n-web-segmented-control', type: Boolean, reflect: true })
  readonly _c11n_web_segmented_control: boolean = true;
  
  @state()
  numberOfSegments: number = 0;
  
  @state()
  pointer: number = 0;
  
  segmentVarSheet: CSSStyleSheet = new CSSStyleSheet();
  
  fieldsetEl!: HTMLFieldSetElement;
  segmentEls!: Array<SegmentComponent>;
  mutationObserver?: MutationObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
    this.shadowRoot?.adoptedStyleSheets.push(this.segmentVarSheet);
    setTimeout(() => {
      this.setMutationObserver();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mutationObserver?.disconnect();
  }

  appendStyles(): void {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-segmented-control\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }

  setMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => this.handleSlotChange());
    this.mutationObserver.observe(this.fieldsetEl, { childList: true, subtree: true }); 
  }

  getPointerValue(): number {
    return this.segmentEls.indexOf(this.segmentEls.find((el) => (el.querySelector('input[type="radio"]') as HTMLInputElement)?.checked) as SegmentComponent) + 1;
  }
  
  handleSlotChange(): void {
    // TODO: get fieldset in a more lit fashion?
    this.fieldsetEl = this.querySelector('fieldset') as HTMLFieldSetElement;
    this.segmentEls = Array.from(this.querySelectorAll(C11nSegment));
    this.numberOfSegments = this.segmentEls.length;
    this.pointer = this.getPointerValue();
  }

  handleClick(event: Event): void {
    const eventTarget = event.target as HTMLElement;
    if (eventTarget.nodeName === 'INPUT') {
      window.requestAnimationFrame(() => this.pointer = this.getPointerValue());
    }
  }

  override render() {
    this.segmentVarSheet.replaceSync(`
      :host {
        --c11n-segments: ${ this.numberOfSegments }; 
        --c11n-segment: ${ this.pointer > 0 ? this.pointer : '1; --pointer-opacity: 0' };
      }
    `);
    return html`
      <slot @click="${this.handleClick}" @slotchange="${this.handleSlotChange}"></slot>
    `;
  }
}

export const C11nSegmentedControl = SegmentedControlComponent.selector;
export const C11nSegmentedControlLiteral = SegmentedControlComponent.literal;
